import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";

const coinLogos = {
  BTC: "https://assets.coingecko.com/coins/images/1/small/bitcoin.png",
  ETH: "https://assets.coingecko.com/coins/images/279/small/ethereum.png",
  BNB: "https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png",
  XRP: "https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png",
  SOL: "https://assets.coingecko.com/coins/images/4128/small/solana.png",
  ADA: "https://assets.coingecko.com/coins/images/975/small/cardano.png",
  DOGE: "https://assets.coingecko.com/coins/images/5/small/dogecoin.png",
  POL: "https://assets.coingecko.com/coins/images/4713/small/matic-token-icon.png",
  LTC: "https://assets.coingecko.com/coins/images/2/small/litecoin.png",
  LINK: "https://assets.coingecko.com/coins/images/877/small/chainlink-new-logo.png",
};

const slugMap = {
  BTC: "bitcoin", ETH: "ethereum", BNB: "bnb", XRP: "xrp",
  SOL: "solana", ADA: "cardano", DOGE: "dogecoin", POL: "polygon",
  LTC: "litecoin", LINK: "chainlink",
};

const SYMBOLS = [
  "BTCUSDT", "ETHUSDT", "BNBUSDT", "XRPUSDT", "SOLUSDT",
  "ADAUSDT", "DOGEUSDT", "POLUSDT", "LTCUSDT", "LINKUSDT"
];

const fetchPricesREST = async () => {
  try {
    const symbolParam = JSON.stringify(SYMBOLS);
    const res = await fetch(
      "https://api.binance.com/api/v3/ticker/24hr?symbols=" + encodeURIComponent(symbolParam)
    );
    const data = await res.json();
    const result = {};
    data.forEach((ticker) => {
      result[ticker.symbol] = {
        price: parseFloat(ticker.lastPrice) || 0,
        change: parseFloat(ticker.priceChangePercent) || 0,
      };
    });
    return result;
  } catch (e) {
    return null;
  }
};

const CryptoTicker = () => {
  const [prices, setPrices] = useState({});
  const wsRef = useRef(null);
  const retryRef = useRef(null);
  const restIntervalRef = useRef(null);

  useEffect(() => {
    // Load REST prices immediately so ticker shows on page load
    fetchPricesREST().then((data) => {
      if (data) setPrices(data);
    });

    // Bots/crawlers get the static REST snapshot only — no live WebSocket,
    // since a never-idle connection prevents prerender from ever finishing
    if (typeof navigator !== "undefined" && navigator.webdriver) {
      return;
    }

    // Try WebSocket for live updates
    const connectWS = () => {
      try {
        const streams = SYMBOLS.map((s) => s.toLowerCase() + "@ticker").join("/");
        const ws = new WebSocket("wss://stream.binance.com:9443/stream?streams=" + streams);

        ws.onopen = () => {
          // WS connected — clear REST polling fallback
          if (restIntervalRef.current) {
            clearInterval(restIntervalRef.current);
            restIntervalRef.current = null;
          }
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            const ticker = data?.data;
            if (!ticker) return;
            setPrices((prev) => ({
              ...prev,
              [ticker.s]: {
                price: parseFloat(ticker.c) || 0,
                change: parseFloat(ticker.P) || 0,
              },
            }));
          } catch (_) {}
        };

        ws.onerror = () => {
          ws.close();
        };

        ws.onclose = () => {
          // Fall back to REST polling every 30 seconds
          if (!restIntervalRef.current) {
            restIntervalRef.current = setInterval(() => {
              fetchPricesREST().then((data) => {
                if (data) setPrices(data);
              });
            }, 30000);
          }
          // Retry WS after 10 seconds
          retryRef.current = setTimeout(connectWS, 10000);
        };

        wsRef.current = ws;
      } catch (_) {
        // If WS fails completely, fall back to REST polling
        if (!restIntervalRef.current) {
          restIntervalRef.current = setInterval(() => {
            fetchPricesREST().then((data) => {
              if (data) setPrices(data);
            });
          }, 30000);
        }
      }
    };

    connectWS();

    return () => {
      if (wsRef.current) wsRef.current.close();
      if (retryRef.current) clearTimeout(retryRef.current);
      if (restIntervalRef.current) clearInterval(restIntervalRef.current);
    };
  }, []);

  const coins = Object.entries(prices || {});
  if (coins.length === 0) return null;

  const tickerData = [...coins, ...coins];

  return (
    <div
      className="w-full overflow-hidden border-b border-[var(--border-soft)]"
      style={{ background: "var(--bg-panel)" }}
    >
      <style>{`
        @keyframes tickerScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-track {
          display: flex;
          width: max-content;
          animation: tickerScroll 30s linear infinite;
          will-change: transform;
        }
        .ticker-track:hover {
          animation-play-state: paused;
        }
        @media (max-width: 768px) {
          .ticker-track {
            animation-duration: 20s;
          }
        }
      `}</style>

      <div className="ticker-track gap-8 px-4 py-2.5">
        {tickerData.map(([symbol, data], index) => {
          if (!symbol || !data) return null;
          const cleanSymbol = symbol.replace("USDT", "");
          const logo = coinLogos[cleanSymbol];
          const price = Number(data?.price ?? 0);
          const change = Number(data?.change ?? 0);
          const slug = slugMap[cleanSymbol] || cleanSymbol.toLowerCase();

          return (
            <Link
              to={"/coins/" + slug}
              key={symbol + "-" + index}
              className="flex items-center gap-2 text-sm hover:opacity-80 transition flex-shrink-0"
            >
              {logo && (
                <img
                  src={logo}
                  alt={cleanSymbol}
                  className="w-5 h-5 flex-shrink-0"
                  onError={(e) => (e.target.style.display = "none")}
                />
              )}
              <span className="font-medium whitespace-nowrap" style={{ color: "var(--text-primary)" }}>
                {cleanSymbol}
              </span>
              <span className="whitespace-nowrap" style={{ color: "var(--text-tertiary)" }}>
                {"$" + (Number.isFinite(price) ? price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: price < 1 ? 4 : 2 }) : "0.00")}
              </span>
              <span className={"whitespace-nowrap " + (change >= 0 ? "text-green-400" : "text-red-400")}>
                {(change >= 0 ? "+" : "") + (Number.isFinite(change) ? change.toFixed(2) : "0.00") + "%"}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default CryptoTicker;